import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Tooltip } from 'react-tooltip';
import { HiOutlineInformationCircle, HiOutlineQuestionMarkCircle } from 'react-icons/hi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, Tooltip as RechartsTooltip } from 'recharts';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import { TOKEN_META } from '../data';

function buildTokenPoolMap(pools) {
  const map = {};
  pools.forEach(pool => {
    pool.tokens.forEach(token => {
      const key = token.toUpperCase();
      if (!map[key]) map[key] = [];
      map[key].push(pool);
    });
  });
  return map;
}

// Build a behavior profile for each token: a vector of normalized metrics across its pools
function buildTokenProfile(pools) {
  if (pools.length === 0) return null;
  const features = pools
    .map(p => [p.apr || 0, p.apyPct1D, p.apyPct7D, p.apyPct30D])
    .filter(f => f[1] != null || f[2] != null || f[3] != null);
  if (features.length === 0) return null;
  return features.flat().filter(v => v != null);
}

function pearson(vecA, vecB) {
  const n = Math.min(vecA.length, vecB.length);
  if (n < 3) return null;
  const a = vecA.slice(0, n);
  const b = vecB.slice(0, n);
  const meanA = a.reduce((s, v) => s + v, 0) / n;
  const meanB = b.reduce((s, v) => s + v, 0) / n;
  let cov = 0, varA = 0, varB = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - meanA;
    const db = b[i] - meanB;
    cov += da * db;
    varA += da * da;
    varB += db * db;
  }
  if (varA === 0 || varB === 0) return 0;
  return Math.max(-1, Math.min(1, cov / (Math.sqrt(varA) * Math.sqrt(varB))));
}

function computeCorrelation(tokenA, tokenB, tokenPoolMap) {
  const poolsA = tokenPoolMap[tokenA] || [];
  const poolsB = tokenPoolMap[tokenB] || [];
  const sharedPools = poolsA.filter(pa => poolsB.some(pb => pb.id === pa.id));

  if (sharedPools.length > 0) {
    const sharedRatio = sharedPools.length / Math.min(poolsA.length, poolsB.length);
    const baseScore = 0.3 + sharedRatio * 0.7;
    return { score: Math.min(1, baseScore), sharedPools: sharedPools.length, dataPoints: sharedPools.length };
  }

  const getMetrics = (pools) => {
    const vals = [];
    pools.forEach(p => {
      if (p.apyPct1D != null) vals.push(p.apyPct1D);
      if (p.apyPct7D != null) vals.push(p.apyPct7D);
      if (p.apyPct30D != null) vals.push(p.apyPct30D);
    });
    return vals;
  };

  const metricsA = getMetrics(poolsA);
  const metricsB = getMetrics(poolsB);

  if (metricsA.length < 3 || metricsB.length < 3) {
    return { score: null, sharedPools: 0, dataPoints: 0 };
  }

  const avgA = metricsA.reduce((s, v) => s + v, 0) / metricsA.length;
  const avgB = metricsB.reduce((s, v) => s + v, 0) / metricsB.length;
  const stdA = Math.sqrt(metricsA.reduce((s, v) => s + (v - avgA) ** 2, 0) / metricsA.length) || 1;
  const stdB = Math.sqrt(metricsB.reduce((s, v) => s + (v - avgB) ** 2, 0) / metricsB.length) || 1;

  const directionSimilarity = (avgA * avgB > 0) ? 1 : -1;
  const magnitudeRatio = Math.min(Math.abs(avgA), Math.abs(avgB)) / (Math.max(Math.abs(avgA), Math.abs(avgB)) || 1);
  const volatilityRatio = Math.min(stdA, stdB) / Math.max(stdA, stdB);

  const profileA = buildTokenProfile(poolsA);
  const profileB = buildTokenProfile(poolsB);
  const pearsonScore = (profileA && profileB) ? pearson(profileA, profileB) : null;

  const heuristicScore = directionSimilarity * magnitudeRatio * volatilityRatio;
  const finalScore = pearsonScore != null
    ? pearsonScore * 0.6 + heuristicScore * 0.4
    : heuristicScore;

  return {
    score: Math.max(-1, Math.min(1, finalScore)),
    sharedPools: 0,
    dataPoints: metricsA.length + metricsB.length,
  };
}

function getCorrelationColor(score, dark) {
  if (score === null) return dark ? '#30363D' : '#E0E0E0';
  if (score > 0.5) return dark ? '#2EA043' : '#0D7A4E';
  if (score > 0.2) return dark ? 'rgba(46,160,67,0.5)' : 'rgba(13,122,78,0.5)';
  if (score > -0.2) return dark ? '#6E7681' : '#AAAAAA';
  if (score > -0.5) return dark ? 'rgba(248,81,73,0.5)' : 'rgba(184,0,26,0.5)';
  return dark ? '#F85149' : '#B8001A';
}

function getCorrelationOpacity(score) {
  if (score === null) return 0.3;
  return 0.3 + Math.abs(score) * 0.7;
}

function getILRisk(correlation) {
  if (correlation === null) return { level: 'UNKNOWN', value: 50 };
  const risk = ((1 - correlation) / 2) * 100;
  let level = 'LOW';
  if (risk > 60) level = 'HIGH';
  else if (risk > 30) level = 'MEDIUM';
  return { level, value: risk };
}

function getCorrelationMeaning(score) {
  if (score === null) return 'Insufficient data to determine correlation';
  if (score > 0.7) return 'High correlation: these tokens tend to move together';
  if (score > 0.3) return 'Moderate correlation: some tendency to move together';
  if (score > -0.3) return 'Low correlation: no strong relationship detected';
  if (score > -0.7) return 'Inverse correlation: tend to move in opposite directions';
  return 'Strong inverse correlation: move in opposite directions';
}

const headingStyle = {
  fontFamily: "'DM Serif Display', serif",
  fontWeight: 400,
};

const monoStyle = {
  fontFamily: "'IBM Plex Mono', monospace",
};

// Animation variants
const cellVariants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: { delay: i * 0.015, duration: 0.3, ease: 'easeOut' },
  }),
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.4, ease: 'easeOut' },
  }),
};

const guideVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.08, duration: 0.35, ease: 'easeOut' },
  }),
};

function CorrelationBarChart({ pairs, dark, colors }) {
  const data = pairs.map(p => ({
    name: `${p.tokenA}/${p.tokenB}`,
    score: parseFloat(p.score.toFixed(3)),
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 30 }} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fill: colors.textSecondary, fontSize: 9, fontFamily: "'IBM Plex Mono', monospace" }}
          angle={-35}
          textAnchor="end"
          interval={0}
          stroke={colors.border}
        />
        <YAxis
          domain={[-1, 1]}
          tick={{ fill: colors.labelMuted, fontSize: 9, fontFamily: "'IBM Plex Mono', monospace" }}
          stroke={colors.border}
          tickCount={5}
        />
        <RechartsTooltip
          contentStyle={{
            background: dark ? '#1C2128' : '#FFFFFF',
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
          }}
          formatter={(value) => [value.toFixed(3), 'Correlation']}
        />
        <Bar dataKey="score" radius={[3, 3, 0, 0]}>
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.score > 0.2 ? (dark ? '#2EA043' : '#0D7A4E') :
                    entry.score < -0.2 ? (dark ? '#F85149' : '#B8001A') :
                    (dark ? '#6E7681' : '#AAAAAA')}
              fillOpacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function CorrelationView({ pools = [], onSelectPool }) {
  const { dark, colors } = useTheme();
  const isMobile = useIsMobile();
  const { tokens, correlationMatrix, poolILData, topPairs, bottomPairs } = useMemo(() => {
    const tpm = buildTokenPoolMap(pools);

    const tokenCounts = Object.entries(tpm)
      .map(([token, poolList]) => ({ token, count: poolList.length }))
      .filter(t => t.count >= 2)
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);

    const tokenList = tokenCounts.map(t => t.token);

    const matrix = {};
    const allPairs = [];
    for (let i = 0; i < tokenList.length; i++) {
      for (let j = i + 1; j < tokenList.length; j++) {
        const key = `${tokenList[i]}|${tokenList[j]}`;
        const result = computeCorrelation(tokenList[i], tokenList[j], tpm);
        matrix[key] = result;
        if (result.score !== null) {
          allPairs.push({ tokenA: tokenList[i], tokenB: tokenList[j], ...result });
        }
      }
    }

    const sorted = [...allPairs].sort((a, b) => b.score - a.score);
    const top5 = sorted.slice(0, 5);
    const bottom5 = sorted.slice(-5).reverse();

    const ilData = pools
      .filter(p => p.tokens.length >= 2)
      .slice(0, 30)
      .map(pool => {
        const t = pool.tokens.map(tk => tk.toUpperCase());
        let totalCorr = 0;
        let corrCount = 0;
        for (let i = 0; i < t.length; i++) {
          for (let j = i + 1; j < t.length; j++) {
            const key1 = `${t[i]}|${t[j]}`;
            const key2 = `${t[j]}|${t[i]}`;
            const entry = matrix[key1] || matrix[key2];
            if (entry && entry.score !== null) {
              totalCorr += entry.score;
              corrCount++;
            }
          }
        }
        const avgCorr = corrCount > 0 ? totalCorr / corrCount : null;
        return { pool, correlation: avgCorr, ilRisk: getILRisk(avgCorr) };
      })
      .filter(d => d.correlation !== null)
      .sort((a, b) => b.ilRisk.value - a.ilRisk.value);

    return { tokens: tokenList, tokenPoolMap: tpm, correlationMatrix: matrix, poolILData: ilData, topPairs: top5, bottomPairs: bottom5 };
  }, [pools]);

  const cellSize = 44;
  const labelWidth = 60;

  const riskColors = {
    LOW: { color: colors.green, bg: colors.greenBg, border: colors.greenBorder },
    MEDIUM: { color: colors.gold, bg: colors.goldBg, border: colors.goldBorder },
    HIGH: { color: colors.red, bg: colors.redBg, border: colors.redBorder },
    UNKNOWN: { color: colors.labelMuted, bg: colors.badgeBg, border: colors.border },
  };

  // Flatten cell index for stagger
  let cellIndex = 0;

  return (
    <div style={{ padding: isMobile ? '12px' : '24px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Tooltip instance */}
      <Tooltip
        id="matrix-cell-tooltip"
        style={{
          background: dark ? '#1C2128' : '#FFFFFF',
          border: `1px solid ${dark ? '#30363D' : '#E8EAED'}`,
          borderRadius: 8,
          padding: '10px 14px',
          boxShadow: `0 4px 12px ${colors.shadow}`,
          zIndex: 200,
          maxWidth: 260,
          opacity: 1,
        }}
        noArrow={false}
        delayShow={80}
      />
      <Tooltip
        id="info-tooltip"
        style={{
          background: dark ? '#1C2128' : '#FFFFFF',
          border: `1px solid ${dark ? '#30363D' : '#E8EAED'}`,
          borderRadius: 6,
          padding: '8px 12px',
          boxShadow: `0 2px 8px ${colors.shadow}`,
          zIndex: 200,
          maxWidth: 240,
          opacity: 1,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: dark ? '#CDD9E5' : '#666666',
        }}
      />

      {/* Header */}
      <div style={{ marginBottom: isMobile ? 20 : 28 }}>
        <h1 style={{
          ...headingStyle,
          fontSize: isMobile ? 22 : 28,
          color: colors.textPrimary,
          margin: '0 0 6px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          Token Correlation Matrix
          <span
            data-tooltip-id="info-tooltip"
            data-tooltip-content="Correlation is computed from yield behavior across shared pools and APY change patterns. Values range from -1 (inverse) to +1 (identical movement)."
            style={{ cursor: 'help', display: 'inline-flex' }}
          >
            <HiOutlineInformationCircle
              size={20}
              color={colors.labelMuted}
              style={{ marginTop: 2 }}
            />
          </span>
        </h1>
        <p style={{
          ...monoStyle,
          fontSize: isMobile ? 11 : 12,
          color: colors.textSecondary,
          margin: '0 0 12px 0',
          lineHeight: 1.6,
          maxWidth: 700,
        }}>
          See how token yields move in relation to each other. Use this to pick smarter LP positions and reduce impermanent loss.
        </p>

        {/* Quick guide cards */}
        <div style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 0,
        }}>
          {[
            { color: colors.green, label: 'Green = Move together', desc: 'Lower IL risk, safer for LPs' },
            { color: colors.red, label: 'Red = Move apart', desc: 'Higher IL risk, but often higher yields' },
            { color: colors.labelMuted, label: 'Gray = No clear pattern', desc: 'Not enough data to determine' },
          ].map((g, i) => (
            <motion.div
              key={i}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={guideVariants}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: 8,
                background: colors.surfaceAlt,
                border: `1px solid ${colors.borderLight}`,
              }}
            >
              <div style={{ width: 10, height: 10, borderRadius: 3, background: g.color, flexShrink: 0 }} />
              <div>
                <div style={{ ...monoStyle, fontSize: 10, fontWeight: 600, color: colors.textPrimary }}>{g.label}</div>
                <div style={{ ...monoStyle, fontSize: 9, color: colors.labelMuted }}>{g.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: isMobile ? 8 : 16,
        marginBottom: 20,
        padding: isMobile ? '8px 12px' : '10px 16px',
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
      }}>
        <span style={{ ...monoStyle, fontSize: 10, color: colors.labelMuted, letterSpacing: 0.5 }}>
          SCALE
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{
            width: 16, height: 16, borderRadius: 3,
            background: dark ? '#F85149' : '#B8001A',
            opacity: 0.9,
          }} />
          <span style={{ ...monoStyle, fontSize: 10, color: colors.textSecondary }}>-1.0</span>
        </div>
        <div style={{
          flex: 1, maxWidth: 200, height: 10, borderRadius: 5,
          background: `linear-gradient(90deg, ${dark ? '#F85149' : '#B8001A'}, ${dark ? '#6E7681' : '#AAAAAA'}, ${dark ? '#2EA043' : '#0D7A4E'})`,
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{
            width: 16, height: 16, borderRadius: 3,
            background: dark ? '#2EA043' : '#0D7A4E',
            opacity: 0.9,
          }} />
          <span style={{ ...monoStyle, fontSize: 10, color: colors.textSecondary }}>+1.0</span>
        </div>
        <div style={{ width: 1, height: 16, background: colors.border, margin: '0 4px' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{
            width: 16, height: 16, borderRadius: 3,
            background: dark ? '#30363D' : '#E0E0E0',
            opacity: 0.5,
          }} />
          <span style={{ ...monoStyle, fontSize: 10, color: colors.textSecondary }}>N/A</span>
        </div>
      </div>

      {/* Correlation Matrix */}
      <div style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: isMobile ? '12px' : '20px',
        marginBottom: isMobile ? 24 : 32,
        overflowX: 'auto',
        position: 'relative',
      }}>
        <h2 style={{
          ...headingStyle,
          fontSize: isMobile ? 16 : 18,
          color: colors.textPrimary,
          margin: '0 0 16px 0',
        }}>
          Yield Correlation Between Tokens
        </h2>

        {tokens.length === 0 ? (
          <p style={{ ...monoStyle, fontSize: 12, color: colors.labelMuted, textAlign: 'center', padding: 40 }}>
            Not enough pool data to compute correlations.
          </p>
        ) : (
          <div style={{ position: 'relative' }}>
            {/* Column headers */}
            <div style={{ display: 'flex', marginLeft: labelWidth }}>
              {tokens.map((token) => (
                <div key={token} style={{
                  width: cellSize,
                  textAlign: 'center',
                  ...monoStyle,
                  fontSize: 9,
                  fontWeight: 600,
                  color: (TOKEN_META[token] || {}).color || colors.textSecondary,
                  letterSpacing: 0.3,
                  paddingBottom: 6,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {token.length > 5 ? token.slice(0, 5) : token}
                </div>
              ))}
            </div>

            {/* Rows */}
            {tokens.map((rowToken, rowIdx) => {
              return (
                <div key={rowToken} style={{ display: 'flex', alignItems: 'center', height: cellSize }}>
                  {/* Row label */}
                  <div style={{
                    width: labelWidth,
                    ...monoStyle,
                    fontSize: 9,
                    fontWeight: 600,
                    color: (TOKEN_META[rowToken] || {}).color || colors.textSecondary,
                    textAlign: 'right',
                    paddingRight: 8,
                    letterSpacing: 0.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {rowToken}
                  </div>
                  {/* Cells */}
                  {tokens.map((colToken, colIdx) => {
                    if (colIdx <= rowIdx) {
                      if (colIdx === rowIdx) {
                        const ci = cellIndex++;
                        return (
                          <motion.div
                            key={colToken}
                            custom={ci}
                            initial="hidden"
                            animate="visible"
                            variants={cellVariants}
                            style={{
                              width: cellSize,
                              height: cellSize,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <div style={{
                              width: cellSize - 6,
                              height: cellSize - 6,
                              borderRadius: 4,
                              background: dark ? 'rgba(46,160,67,0.15)' : 'rgba(13,122,78,0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              ...monoStyle,
                              fontSize: 8,
                              color: colors.labelMuted,
                            }}>
                              1.0
                            </div>
                          </motion.div>
                        );
                      }
                      return <div key={colToken} style={{ width: cellSize, height: cellSize }} />;
                    }

                    const key = `${rowToken}|${colToken}`;
                    const keyAlt = `${colToken}|${rowToken}`;
                    const data = correlationMatrix[key] || correlationMatrix[keyAlt] || { score: null, sharedPools: 0, dataPoints: 0 };
                    const cellColor = getCorrelationColor(data.score, dark);
                    const opacity = getCorrelationOpacity(data.score);
                    const ci = cellIndex++;
                    const cellId = `cell-${rowToken}-${colToken}`;
                    const meaning = getCorrelationMeaning(data.score);

                    // Build tooltip HTML
                    const tooltipHtml = `
                      <div style="font-family: 'IBM Plex Mono', monospace;">
                        <div style="font-size: 12px; font-weight: 600; margin-bottom: 6px;">${rowToken} &harr; ${colToken}</div>
                        <div style="display: flex; justify-content: space-between; gap: 16px; margin-bottom: 3px;">
                          <span style="font-size: 10px; opacity: 0.6;">Correlation</span>
                          <span style="font-size: 10px; font-weight: 600; color: ${data.score !== null ? (data.score > 0.2 ? '#0D7A4E' : data.score < -0.2 ? '#B8001A' : 'inherit') : 'inherit'};">${data.score !== null ? data.score.toFixed(3) : 'N/A'}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; gap: 16px; margin-bottom: 3px;">
                          <span style="font-size: 10px; opacity: 0.6;">Shared Pools</span>
                          <span style="font-size: 10px;">${data.sharedPools}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; gap: 16px; margin-bottom: 6px;">
                          <span style="font-size: 10px; opacity: 0.6;">Data Points</span>
                          <span style="font-size: 10px;">${data.dataPoints}</span>
                        </div>
                        <div style="font-size: 9px; opacity: 0.7; border-top: 1px solid ${dark ? '#30363D' : '#E8EAED'}; padding-top: 5px; line-height: 1.4;">${meaning}</div>
                      </div>
                    `;

                    return (
                      <motion.div
                        key={colToken}
                        custom={ci}
                        initial="hidden"
                        animate="visible"
                        variants={cellVariants}
                        whileHover={{ scale: 1.18, zIndex: 10 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        style={{
                          width: cellSize,
                          height: cellSize,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                        data-tooltip-id="matrix-cell-tooltip"
                        data-tooltip-html={tooltipHtml}
                        id={cellId}
                      >
                        <div style={{
                          width: cellSize - 6,
                          height: cellSize - 6,
                          borderRadius: 4,
                          background: cellColor,
                          opacity,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'opacity 0.15s ease',
                        }}>
                          <span style={{
                            ...monoStyle,
                            fontSize: 8,
                            fontWeight: 600,
                            color: '#FFFFFF',
                            opacity: 0.9,
                          }}>
                            {data.score !== null ? data.score.toFixed(1) : '\u2014'}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Top/Bottom Correlated Pairs Charts */}
      {(topPairs.length > 0 || bottomPairs.length > 0) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 16,
          marginBottom: isMobile ? 24 : 32,
        }}>
          {topPairs.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              style={{
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: isMobile ? '12px' : '20px',
              }}
            >
              <h3 style={{
                ...headingStyle,
                fontSize: isMobile ? 14 : 16,
                color: colors.textPrimary,
                margin: '0 0 4px 0',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                Most Correlated Pairs
                <span
                  data-tooltip-id="info-tooltip"
                  data-tooltip-content="These pairs move together the most. Great candidates for low-IL liquidity provision."
                  style={{ cursor: 'help', display: 'inline-flex' }}
                >
                  <HiOutlineInformationCircle size={14} color={colors.green} />
                </span>
              </h3>
              <p style={{ ...monoStyle, fontSize: 10, color: colors.labelMuted, margin: '0 0 12px 0' }}>
                Lower impermanent loss risk
              </p>
              <CorrelationBarChart pairs={topPairs} dark={dark} colors={colors} />
            </motion.div>
          )}
          {bottomPairs.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              style={{
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: isMobile ? '12px' : '20px',
              }}
            >
              <h3 style={{
                ...headingStyle,
                fontSize: isMobile ? 14 : 16,
                color: colors.textPrimary,
                margin: '0 0 4px 0',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                Least Correlated Pairs
                <span
                  data-tooltip-id="info-tooltip"
                  data-tooltip-content="These pairs diverge the most. Higher IL risk but potentially higher APR rewards."
                  style={{ cursor: 'help', display: 'inline-flex' }}
                >
                  <HiOutlineInformationCircle size={14} color={colors.red} />
                </span>
              </h3>
              <p style={{ ...monoStyle, fontSize: 10, color: colors.labelMuted, margin: '0 0 12px 0' }}>
                Higher impermanent loss risk
              </p>
              <CorrelationBarChart pairs={bottomPairs} dark={dark} colors={colors} />
            </motion.div>
          )}
        </div>
      )}

      {/* Impermanent Loss Risk Indicator */}
      <div style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: isMobile ? '12px' : '20px',
        marginBottom: isMobile ? 24 : 32,
        overflowX: 'auto',
      }}>
        <h2 style={{
          ...headingStyle,
          fontSize: isMobile ? 16 : 18,
          color: colors.textPrimary,
          margin: '0 0 4px 0',
        }}>
          Which Pools Have the Most IL Risk?
        </h2>
        <p style={{
          ...monoStyle,
          fontSize: 11,
          color: colors.textSecondary,
          margin: '0 0 16px 0',
        }}>
          Pools where tokens move in different directions have higher impermanent loss risk. Green = safer, Red = riskier.
        </p>

        {poolILData.length === 0 ? (
          <p style={{ ...monoStyle, fontSize: 12, color: colors.labelMuted, textAlign: 'center', padding: 20 }}>
            Not enough data to compute IL risk indicators.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1.6fr 0.8fr 1.2fr 0.5fr',
              gap: 12,
              padding: '6px 12px',
              borderBottom: `1px solid ${colors.border}`,
            }}>
              {['Pool', 'Correlation', 'IL Risk', 'APR'].map(label => (
                <span key={label} style={{
                  ...monoStyle,
                  fontSize: 9,
                  fontWeight: 600,
                  color: colors.labelMuted,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                }}>
                  {label}
                </span>
              ))}
            </div>

            {poolILData.map(({ pool, correlation, ilRisk }) => {
              const rc = riskColors[ilRisk.level];
              return (
                <div
                  key={pool.id}
                  onClick={() => onSelectPool && onSelectPool(pool)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.6fr 0.8fr 1.2fr 0.5fr',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'background 0.15s ease',
                    background: 'transparent',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = colors.hover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {/* Pool name */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{
                      ...monoStyle,
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.textPrimary,
                    }}>
                      {pool.name}
                    </span>
                    <span style={{
                      ...monoStyle,
                      fontSize: 9,
                      color: colors.labelMuted,
                    }}>
                      {pool.protocol} · {pool.chain}
                    </span>
                  </div>

                  {/* Correlation score */}
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{
                      ...monoStyle,
                      fontSize: 12,
                      fontWeight: 600,
                      color: correlation > 0.2 ? colors.green : correlation < -0.2 ? colors.red : colors.textSecondary,
                    }}>
                      {(correlation || 0).toFixed(3)}
                    </span>
                  </div>

                  {/* IL Risk gauge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      flex: 1,
                      height: 8,
                      borderRadius: 4,
                      background: colors.scoreTrack,
                      overflow: 'hidden',
                      maxWidth: 120,
                    }}>
                      <div style={{
                        width: `${Math.min(100, ilRisk.value)}%`,
                        height: '100%',
                        borderRadius: 4,
                        background: rc.color,
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                    <span style={{
                      ...monoStyle,
                      fontSize: 9,
                      fontWeight: 600,
                      color: rc.color,
                      background: rc.bg,
                      border: `1px solid ${rc.border}`,
                      padding: '2px 6px',
                      borderRadius: 4,
                      letterSpacing: 0.5,
                      whiteSpace: 'nowrap',
                    }}>
                      {ilRisk.level}
                    </span>
                  </div>

                  {/* APR */}
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{
                      ...monoStyle,
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.green,
                    }}>
                      {(pool.apr || 0).toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Explanatory section */}
      <div style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: isMobile ? '12px' : '20px',
      }}>
        <h2 style={{
          ...headingStyle,
          fontSize: isMobile ? 16 : 18,
          color: colors.textPrimary,
          margin: '0 0 12px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <HiOutlineQuestionMarkCircle size={20} color={colors.textSecondary} />
          How to Use This
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          {[
            {
              title: 'What does correlation mean?',
              text: '+1.0 = tokens move together (e.g. USDC/USDT). -1.0 = they move in opposite directions. 0 = no clear relationship. The closer to +1.0, the safer for LPs.',
              accent: colors.textSecondary,
            },
            {
              title: 'Why should I care?',
              text: 'When you provide liquidity, you lose money (impermanent loss) if the two tokens in your pool move apart in price. Correlated pairs = less IL = safer positions.',
              accent: colors.green,
            },
            {
              title: 'Safe pairs (green cells)',
              text: 'Stablecoin pairs (USDC/USDT) or wrapped variants (ETH/wstETH) have high correlation. Great for low-risk yield farming with minimal IL.',
              accent: colors.green,
            },
            {
              title: 'Risky pairs (red cells)',
              text: 'Pairs like ETH/DOGE can move in opposite directions, causing significant IL. The upside? These pools often pay higher APR to compensate.',
              accent: colors.red,
            },
          ].map((card, i) => (
            <motion.div
              key={i}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={cardVariants}
              style={{
                padding: '14px 16px',
                borderRadius: 8,
                background: colors.surfaceAlt,
                border: `1px solid ${colors.borderLight}`,
              }}
            >
              <h3 style={{
                ...monoStyle,
                fontSize: 11,
                fontWeight: 600,
                color: card.accent,
                margin: '0 0 6px 0',
                letterSpacing: 0.3,
              }}>
                {card.title}
              </h3>
              <p style={{
                ...monoStyle,
                fontSize: 11,
                color: colors.textSecondary,
                margin: 0,
                lineHeight: 1.6,
              }}>
                {card.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
